import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { CerereTypeService } from '../cerere-type.service';
import { IRequestTypeAddPayload, IRequestTypeUpdatePayload } from '../cerere-type.interface';
import { departments } from '../departamente.model';
import { helper } from "../../../../../environments/helper";
import { Observable, map, finalize, Subject, of, switchMap, distinctUntilChanged, takeUntil, tap, catchError } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { CererePriorityService } from '../../cerere-priority/cerere-priority.service';
import { StatusService } from '../../status-cerere/status.service';

@Component({
    selector: 'app-cerere-request-type',
    templateUrl: './edit-cerere-type.component.html',
    styleUrls: ['./edit-cerere-type.component.scss'],
})
export class EditCerereTypeComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    departments = departments;

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.data.name, [Validators.required]),
        slug: new FormControl(this.data.data.slug, [Validators.required]),
        id_priority: new FormControl(this.data.data.id_priority, [Validators.required]),
        id_status: new FormControl(this.data.data.id_status, [Validators.required]),
        directie_id: new FormControl(this.data.data.id_directie, [Validators.required]),
        compartiment_id: new FormControl(this.data.data.id_compartiment ? this.data.data.id_compartiment : -1, []),
        user_id: new FormControl(this.data.data.user_id ? this.data.data.user_id : -1, []),
        indoc_name: new FormControl(this.data.data.indoc_name, []),
        indoc_id: new FormControl(this.data.data.indoc_id, []),
    });

    priorities$!: Observable<Generic[]>;
    statuses$!: Observable<Generic[]>;
    directiiActive: Generic[] = this.data.directiiActive;
    compartimenteActive!: Generic[];
    usersActive!: Generic[];

    private editDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data.directiiActive;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<EditCerereTypeComponent>,
        private requestTypesService: CerereTypeService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private commonService: CommonService,
        private cererePriorityService: CererePriorityService,
        private statusService: StatusService,
        private fb: FormBuilder,
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.loadStatuses();
        this.loadPriorities();
        this.renderFieldsSearch();

        if (this.data.data.id_directie) {
            this.loadCompartimenteActive(this.data.data.id_directie);
        }

        this.loadUsers();
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['user_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimenteActive = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }
    }

    loadUsers() {
        this.isLoading = true;
        let formValues = { ...this.form.value };
        let formValuesForm: FormGroup = this.fb.group(formValues);

        if (formValuesForm.get('compartiment_id')?.value === '-1') {
            formValuesForm.get('compartiment_id')?.setValue(null);
        }

        this.commonService
            .getUsersByOrganigrama(formValuesForm.value)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadStatuses() {
        this.isLoading = true;
        this.statuses$ = this.statusService
            .getStatuses()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    loadPriorities() {
        this.isLoading = true;
        this.priorities$ = this.cererePriorityService
            .getCererePriorities()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.requestTypesService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            slug,
            id_priority,
            id_status,
            directie_id,
            compartiment_id,
            user_id,
            indoc_id,
            indoc_name
        } = this.form.value as IRequestTypeAddPayload;

        const updateRequestType: IRequestTypeUpdatePayload = {
            id_cerere_type: this.data.data.id_cerere_type,
            name,
            slug,
            id_priority,
            id_status,
            directie_id,
            compartiment_id,
            user_id,
            indoc_id,
            indoc_name
        };

        this.requestTypesService
            .updateRequestType(updateRequestType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0]
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onHttpError(res: any) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    msg,
                    this.errorIcon
                );
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }
}
